import type { FC, ReactNode } from "react";
import { useEffect, useRef, useState } from "react";
import { useMeasure } from "@uidotdev/usehooks";
import styled from "@emotion/styled";
import { colors, spacing } from "../design-tokens";
import { ActionButton } from "../primitives/action-button";
import { COLLAPSED_CONTENT_HEIGHT } from "@/constants/search-constants";

const Container = styled.div<{ isExpanded: boolean; maxHeight: number }>`
  position: relative;
  width: 100%;
  overflow: hidden;
  transition: max-height 0.3s ease-in-out;
  max-height: ${(props) =>
    props.isExpanded
      ? `${props.maxHeight}px`
      : `${COLLAPSED_CONTENT_HEIGHT}px`};
`;

const Content = styled.div`
  /* Ensure content starts from top without any gaps */
  display: flow-root; /* Creates a new block formatting context */
`;

const GradientOverlay = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-end;
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: ${COLLAPSED_CONTENT_HEIGHT / 2}px;
  background: linear-gradient(
    to bottom,
    ${colors.surface}00 0%,
    ${colors.surface}80 20%,
    ${colors.surface}FF 80%,
    ${colors.surface}FF 100%
  );
  opacity: 1;
`;

const StyledActionButton = styled(ActionButton)`
  pointer-events: auto;
  margin-bottom: ${spacing.sm}px;
  text-decoration: none;
  font-weight: 600;
  font-size: 18px;

  &:hover {
    text-decoration: underline;
    color: ${colors.primaryHover};
  }
`;

type CollapsedResponseContainerProps = {
  shouldCollapseInitially?: boolean;
  children: ReactNode;
};

export const CollapsedResponseContainer: FC<
  CollapsedResponseContainerProps
> = ({ children, shouldCollapseInitially = false }) => {
  const userHasExpanded = useRef(false);
  const [ref, { height }] = useMeasure();

  const [shouldShowReadMore, setShouldShowReadMore] = useState(
    shouldCollapseInitially
  );

  useEffect(() => {
    if (height && height >= COLLAPSED_CONTENT_HEIGHT) {
      if (shouldCollapseInitially && !userHasExpanded.current) {
        setShouldShowReadMore(true);
      }
    } else {
      setShouldShowReadMore(false);
    }
  }, [height]);

  const handleToggle = () => {
    setShouldShowReadMore(false);
    userHasExpanded.current = true;
  };

  return (
    <Container isExpanded={!shouldShowReadMore} maxHeight={height || 0}>
      <Content ref={ref}>{children}</Content>
      {shouldShowReadMore && (
        <GradientOverlay>
          <StyledActionButton
            onClick={handleToggle}
            aria-label="Read more content"
            variant="ghost"
          >
            Read more
          </StyledActionButton>
        </GradientOverlay>
      )}
    </Container>
  );
};
